import React, { useEffect, useMemo } from 'react';

import { InformedPickerField } from '@nw/design-system/omega';
import { LOADING } from '@nw/shared/utils';

import {
  isYetToBeDecidedUniversityValue,
  YET_TO_BE_DECIDED_UNIVERSITY_LABEL,
  YET_TO_BE_DECIDED_UNIVERSITY_VALUE
} from '../../constants/ApplicationFeeConstants';
import { useNiatApplicationMachineContext } from '../../context/NiatApplicationMachineProvider';
import { getInstitutesWithSelectableCourses } from '../../utils/enrollmentCollegeSelectionUtils';

import { FieldContainer } from '../ApplicationSectionsPage/styledComponents';
import { TakeAdmissionInlineFieldsRow } from '../ProductFeeSectionCommonComponents/styledComponents';

const instituteAndAccommodationFieldCss = `
  max-width: 50%;
  box-sizing: border-box;

  @media (max-width: 769px) {
    max-width: 100%;
  }
`;

interface InstituteAndAccomodationProps {
  formProps: any;
  validationSchemaObject: any;
  includeYetToDecide?: boolean;
  filterZeroSeatCourses?: boolean;
  preferredUniversityField?: string;
  accommodationModeField?: string;
  onSelectionChange?: (values: {
    preferredUniversity: string;
    accommodationMode: string;
  }) => void;
}

const InstituteAndAccomodation = ({
  formProps,
  validationSchemaObject,
  includeYetToDecide = true,
  filterZeroSeatCourses = false,
  preferredUniversityField = 'preferredUniversity',
  accommodationModeField = 'accommodationMode',
  onSelectionChange
}: InstituteAndAccomodationProps) => {
  const { niatApplicationMachineState, niatApplicationMachineSend } =
    useNiatApplicationMachineContext();
  const { formState } = formProps;
  const { values, errors, touched } = formState as any;
  const {
    availableProgramInstitutesData,
    graphQlApiStatus: {
      getAvailableProgramInstitutesApiStatus,
      getUserOrdersDetailsApiStatus
    }
  } = niatApplicationMachineState.context;
  const programInstitutesData = availableProgramInstitutesData;
  const isProgramInstitutesLoading =
    getAvailableProgramInstitutesApiStatus === LOADING;

  useEffect(() => {
    if (
      getUserOrdersDetailsApiStatus === LOADING ||
      getAvailableProgramInstitutesApiStatus === LOADING ||
      availableProgramInstitutesData?.institutes?.length
    ) {
      return;
    }
    niatApplicationMachineSend({
      type: 'GET_AVAILABLE_PROGRAM_INSTITUTES'
    });
  }, [
    availableProgramInstitutesData?.institutes?.length,
    getAvailableProgramInstitutesApiStatus,
    getUserOrdersDetailsApiStatus,
    niatApplicationMachineSend
  ]);
  const preferredUniversity = String(values?.[preferredUniversityField] ?? '');
  const accommodationMode = String(values?.[accommodationModeField] ?? '');

  const takeAdmissionCollegeOptions = useMemo(() => {
    const allInstitutes = programInstitutesData?.institutes ?? [];
    const list = filterZeroSeatCourses
      ? getInstitutesWithSelectableCourses(allInstitutes)
      : allInstitutes;
    const instituteOptions = list.map((inst) => ({
      label: inst.institute_name,
      value: inst.institute_id
    }));

    if (!includeYetToDecide) {
      return instituteOptions;
    }

    return [
      ...instituteOptions,
      {
        label: YET_TO_BE_DECIDED_UNIVERSITY_LABEL,
        value: YET_TO_BE_DECIDED_UNIVERSITY_VALUE
      }
    ];
  }, [filterZeroSeatCourses, includeYetToDecide, programInstitutesData]);

  const takeAdmissionAccommodationOptions = useMemo(() => {
    if (
      !preferredUniversity ||
      isYetToBeDecidedUniversityValue(preferredUniversity)
    ) {
      return [];
    }
    const institute = programInstitutesData?.institutes?.find(
      (inst) => inst.institute_id === preferredUniversity
    );
    if (!institute?.accommodation_modes?.length) {
      return [];
    }
    return institute.accommodation_modes.map((mode) => ({
      label:
        mode === 'HOSTELER'
          ? 'Hosteler'
          : mode === 'DAY_SCHOLAR'
            ? 'Day Scholar'
            : '',
      value: mode
    }));
  }, [preferredUniversity, programInstitutesData]);

  useEffect(() => {
    onSelectionChange?.({
      preferredUniversity,
      accommodationMode
    });
  }, [accommodationMode, onSelectionChange, preferredUniversity]);

  return (
    <TakeAdmissionInlineFieldsRow>
      <FieldContainer css={instituteAndAccommodationFieldCss}>
        <InformedPickerField
          isDisabled={isProgramInstitutesLoading}
          isHidden={false}
          field={preferredUniversityField}
          key={preferredUniversityField}
          validationSchemaObject={validationSchemaObject}
          label="Preferred University/College"
          placeholder="Select preferred university"
          options={takeAdmissionCollegeOptions}
          error={errors[preferredUniversityField]}
          touched={touched?.[preferredUniversityField]}
          reRenderObjects={[programInstitutesData, includeYetToDecide, filterZeroSeatCourses]}
          isMarginBottomZero={true}
          onChangeOption={() => {
            formProps.formApi.setValue(accommodationModeField, '');
          }}
        />
      </FieldContainer>
      <FieldContainer css={instituteAndAccommodationFieldCss}>
        <InformedPickerField
          isDisabled={
            isProgramInstitutesLoading ||
            !preferredUniversity ||
            isYetToBeDecidedUniversityValue(preferredUniversity)
          }
          isHidden={false}
          field={accommodationModeField}
          key={`${accommodationModeField}-${preferredUniversity}`}
          validationSchemaObject={validationSchemaObject}
          label="Accommodation mode"
          placeholder="Select Accommodation mode"
          options={takeAdmissionAccommodationOptions}
          error={errors[accommodationModeField]}
          touched={touched?.[accommodationModeField]}
          reRenderObjects={[preferredUniversity, programInstitutesData]}
          isMarginBottomZero={true}
        />
      </FieldContainer>
    </TakeAdmissionInlineFieldsRow>
  );
};

export default InstituteAndAccomodation;
